// ============================================================
// Router Configuration
// ============================================================

import { createBrowserRouter } from 'react-router-dom';
import { RootLayout } from '@/components/layout/RootLayout';
import { AuthLayout } from '@/components/layout/AuthLayout';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { SuperAdminLayout } from '@/components/layout/SuperAdminLayout';
import { BlankLayout } from '@/components/layout/BlankLayout';
import { ProtectedRoute } from '@/auth/ProtectedRoute';
import { PERMISSIONS } from '@/constants/permissions';
import { ROLES } from '@/constants/roles';

// Feature Pages
import { LoginPage } from '@/features/auth/LoginPage';
import { ForgotPasswordPage } from '@/features/auth/ForgotPasswordPage';
import { UnauthorizedPage } from '@/features/auth/UnauthorizedPage';
import { NotFoundPage } from '@/features/auth/NotFoundPage';
import { DashboardPage } from '@/features/dashboard/DashboardPage';
import { StudentsLayout } from '@/features/students/StudentsLayout';
import { StudentDashboardPage } from '@/features/students/StudentDashboardPage';
import { StudentListPage } from '@/features/students/StudentListPage';
import { AddStudentPage } from '@/features/students/AddStudentPage';
import { EditStudentPage } from '@/features/students/EditStudentPage';
import { StudentProfilePage } from '@/features/students/StudentProfilePage';
import { PromoteStudentsPage } from '@/features/students/PromoteStudentsPage';

import { AcademicSessionListPage } from '@/features/academic-sessions/AcademicSessionListPage';
import { SettingsPage } from '@/features/settings/SettingsPage';

// Super Admin Pages
import { SuperAdminDashboardPage } from '@/features/super-admin/dashboard/SuperAdminDashboardPage';
import { OrganizationListPage } from '@/features/super-admin/organizations/OrganizationListPage';
import { CreateOrganizationPage } from '@/features/super-admin/organizations/CreateOrganizationPage';
import { OrganizationDetailsPage } from '@/features/super-admin/organizations/OrganizationDetailsPage';
import { GlobalSchoolListPage } from '@/features/super-admin/schools/GlobalSchoolListPage';
import { CreateGlobalSchoolPage } from '@/features/super-admin/schools/CreateGlobalSchoolPage';
import { GlobalSchoolDetailsPage } from '@/features/super-admin/schools/GlobalSchoolDetailsPage';
import { GlobalSchoolConfigurationPage } from '@/features/super-admin/schools/GlobalSchoolConfigurationPage';
import { ModuleAssignmentPage } from '@/features/super-admin/modules/ModuleAssignmentPage';
import { RoleAccessPage } from '@/features/super-admin/roles/RoleAccessPage';
import { GlobalUsersPage } from '@/features/super-admin/users/GlobalUsersPage';

import { FeesLayout } from '@/features/fees/FeesLayout';
import { FeeDashboardPage } from '@/features/fees/FeeDashboardPage';
import { FeeTypesPage } from '@/features/fees/FeeTypesPage';
import { FeeStructurePage } from '@/features/fees/FeeStructurePage';
import { AddFeeStructurePage } from '@/features/fees/AddFeeStructurePage';
import { FeeStructureComponentsPage } from '@/features/fees/FeeStructureComponentsPage';
import { FeeInstallmentsPage } from '@/features/fees/FeeInstallmentsPage';
import { AssignFeePage } from '@/features/fees/AssignFeePage';
import { CollectFeePage } from '@/features/fees/CollectFeePage';
import { StudentFeeCollectionPage } from '@/features/fees/StudentFeeCollectionPage';
import { StudentFeeStatusDetailPage } from '@/features/fees/StudentFeeStatusDetailPage';

import { SchoolsLayout } from '@/features/schools/SchoolsLayout';
import { SchoolListPage } from '@/features/schools/SchoolListPage';
import { SchoolDetailsPage } from '@/features/schools/SchoolDetailsPage';
import { SchoolComparisonPage } from '@/features/schools/SchoolComparisonPage';

// Teacher Management
import { TeachersLayout } from '@/features/teachers/TeachersLayout';
import { TeacherListPage } from '@/features/teachers/TeacherListPage';
import { AddTeacherPage } from '@/features/teachers/AddTeacherPage';
import { EditTeacherPage } from '@/features/teachers/EditTeacherPage';
import { TeacherProfilePage } from '@/features/teachers/TeacherProfilePage';
import { TeacherAssignmentsPage } from '@/features/teachers/TeacherAssignmentsPage';
import { TeacherDirectoryPage } from '@/features/teachers/TeacherDirectoryPage';
import { TeacherDashboardPage } from '@/features/teachers/TeacherDashboardPage';

// Staff Management
import { StaffManagementLayout } from '@/features/staff-management/StaffManagementLayout';

import { DepartmentsPage } from '@/features/staff-management/DepartmentsPage';
import { DesignationsPage } from '@/features/staff-management/DesignationsPage';
import { LeaveTypesPage } from '@/features/staff-management/LeaveTypesPage';
import { StaffDisciplinePage } from '@/features/staff-management/StaffDisciplinePage';
import { StaffListPage } from '@/features/staff-management/StaffListPage';
import { AddEditStaffPage } from '@/features/staff-management/AddEditStaffPage';
import { StaffProfilePage } from '@/features/staff-management/StaffProfilePage';

// Leaves
import { LeavesLayout } from '@/features/leaves/LeavesLayout';
import { LeaveDashboardPage } from '@/features/leaves/LeaveDashboardPage';
import { LeaveRequestsPage } from '@/features/leaves/LeaveRequestsPage';
import { MyLeavesPage } from '@/features/leaves/MyLeavesPage';

// Teacher Workspace
import { MyClassesPage } from '@/features/teachers/workspace/MyClassesPage';
import { MyStudentsPage } from '@/features/teachers/workspace/MyStudentsPage';

import { HomeworkPage } from '@/features/teachers/workspace/HomeworkPage';
import { ClassworkDiaryPage } from '@/features/teachers/workspace/ClassworkDiaryPage';
import { MyTimetablePage } from '@/features/teachers/workspace/MyTimetablePage';
import { TeacherNotificationsPage } from '@/features/teachers/workspace/TeacherNotificationsPage';
import { MyProfilePage } from '@/features/teachers/workspace/MyProfilePage';

// Attendance
import { AttendanceLayout } from '@/features/attendance/AttendanceLayout';
import { DailyAttendancePage } from '@/features/attendance/DailyAttendancePage';
import { StaffAttendanceDetailsPage } from '@/features/attendance/StaffAttendanceDetailsPage';
import { AttendanceSettingsPage } from '@/features/attendance/AttendanceSettingsPage';

import { MyAttendanceHistoryPage } from '@/features/attendance/MyAttendanceHistoryPage';
import { MyAttendancePage } from '@/features/attendance/MyAttendancePage';
import { StudentAttendanceSettingsPage } from '@/features/attendance/StudentAttendanceSettingsPage';
import { StudentAttendanceDailyPage } from '@/features/attendance/StudentAttendanceDailyPage';

import { ExpenseListPage } from '@/features/expenses/ExpenseListPage';
import { ExpenseCategoryPage } from '@/features/expenses/ExpenseCategoryPage';
import { ExpenseDashboardPage } from '@/features/expenses/ExpenseDashboardPage';

import { IncomeListPage } from '@/features/incomes/IncomeListPage';
import { IncomeCategoryPage } from '@/features/incomes/IncomeCategoryPage';
import { IncomeDashboardPage } from '@/features/incomes/IncomeDashboardPage';
import { PublicWebsitePage } from '@/features/public-website/PublicWebsitePage';

const router = createBrowserRouter([
  {
    // Root layout provides Auth, Permission, School, and Public School contexts
    element: <RootLayout />,
    children: [
      // ── Public School Website ─────────────────────
      {
        path: '/',
        element: <PublicWebsitePage />,
      },

      // ── Public Auth Routes ─────────────────────────
      {
        element: <AuthLayout />,
        children: [
          {
            path: '/login',
            element: <LoginPage />,
          },
          {
            path: '/ERP',
            element: <LoginPage />,
          },
          {
            path: '/forgot-password',
            element: <ForgotPasswordPage />,
          },
        ],
      },

      // ── Protected Dashboard Routes ────────────────
      {
        element: <ProtectedRoute requiredPermission={PERMISSIONS.DASHBOARD_VIEW} />,
        children: [
          {
            element: <DashboardLayout />,
            children: [
              {
                path: '/dashboard',
                element: <DashboardPage />,
              },
            ],
          }
        ],
      },

      // ── Protected Super Admin Routes ──────────────
      {
        element: <ProtectedRoute requiredPermission={PERMISSIONS.SUPER_ADMIN_ACCESS} />,
        children: [
          {
            element: <SuperAdminLayout />,
            children: [
              { path: '/super-admin/dashboard', element: <SuperAdminDashboardPage /> },
              { path: '/super-admin/organizations', element: <OrganizationListPage /> },
              { path: '/super-admin/organizations/add', element: <CreateOrganizationPage /> },
              { path: '/super-admin/organizations/edit/:id', element: <CreateOrganizationPage /> },
              { path: '/super-admin/organizations/:id', element: <OrganizationDetailsPage /> },
              { path: '/super-admin/schools', element: <GlobalSchoolListPage /> },
              { path: '/super-admin/schools/add', element: <CreateGlobalSchoolPage /> },
              { path: '/super-admin/schools/edit/:id', element: <CreateGlobalSchoolPage /> },
              { path: '/super-admin/schools/:id', element: <GlobalSchoolDetailsPage /> },
              { path: '/super-admin/schools/configure/:id', element: <GlobalSchoolConfigurationPage /> },
              { path: '/super-admin/modules', element: <ModuleAssignmentPage /> },
              { path: '/super-admin/role-access', element: <RoleAccessPage /> },
              { path: '/super-admin/users', element: <GlobalUsersPage /> },
              // Reports, Settings can be added later
            ],
          }
        ],
      },

      // ── Protected Academic Sessions Routes ────────
      {
        element: <ProtectedRoute requiredPermission={PERMISSIONS.ACADEMIC_SESSION_MANAGE} />,
        children: [
          {
            element: <DashboardLayout />,
            children: [
              {
                path: '/academic-sessions',
                element: <AcademicSessionListPage />,
              },
              {
                path: '/settings/academic-session',
                element: <AcademicSessionListPage />,
              },
              {
                path: '/settings/academic-sessions',
                element: <AcademicSessionListPage />,
              },
            ],
          }
        ],
      },

      // ── Protected Students Routes ─────────────────
      {
        element: <ProtectedRoute requiredPermission={PERMISSIONS.STUDENT_VIEW} />,
        children: [
          {
            element: <DashboardLayout />,
            children: [
              {
                path: '/students',
                element: <StudentsLayout />,
                children: [
                  { index: true, element: <StudentDashboardPage /> },
                  { path: 'dashboard', element: <StudentDashboardPage /> },
                  { path: 'list', element: <StudentListPage /> },
                  { path: 'add', element: <AddStudentPage /> },
                  { path: 'edit/:id', element: <EditStudentPage /> },
                  { path: 'promote', element: <PromoteStudentsPage /> },
                  { path: 'transfer', element: <NotFoundPage /> },
                  { path: ':id', element: <StudentProfilePage /> },
                ]
              },
            ],
          }
        ],
      },
      // ── Protected Fees Routes ─────────────────────
      {
        element: <ProtectedRoute requiredPermission={PERMISSIONS.FEE_VIEW} />,
        children: [
          {
            element: <DashboardLayout />,
            children: [
              {
                path: '/fees',
                element: <FeesLayout />,
                children: [
                  { index: true, element: <FeeDashboardPage /> },
                  { path: 'dashboard', element: <FeeDashboardPage /> },
                  { path: 'types', element: <FeeTypesPage /> },
                  { path: 'structure', element: <FeeStructurePage /> },
                  { path: 'structure/add', element: <AddFeeStructurePage /> },
                  { path: 'structure/edit/:id', element: <AddFeeStructurePage /> },
                  { path: 'structure/:id/components', element: <FeeStructureComponentsPage /> },
                  { path: 'structure/:structureId/components/:itemId/installments', element: <FeeInstallmentsPage /> },
                  { path: 'assign', element: <AssignFeePage /> },
                  { path: 'collect', element: <CollectFeePage /> },
                  { path: 'collect/:studentId', element: <StudentFeeCollectionPage /> },
                  { path: 'student-status/:studentId', element: <StudentFeeStatusDetailPage /> },
                ]
              },
            ],
          }
        ],
      },

      // ── Protected Schools Routes (Organization Admin Only) ─────────────────────
      {
        element: <ProtectedRoute allowedRoles={['ORGANIZATION_ADMIN', ROLES.ORGANIZATION_ADMIN]} />,
        children: [
          {
            element: <DashboardLayout />,
            children: [
              {
                path: '/schools',
                element: <SchoolsLayout />,
                children: [
                  { index: true, element: <SchoolListPage /> },
                  { path: 'compare', element: <SchoolComparisonPage /> },
                  { path: ':id', element: <SchoolDetailsPage /> },
                ]
              },
            ],
          }
        ],
      },
      
      // ── Protected Teacher Management Routes ──────────
      {
        element: <ProtectedRoute requiredPermission={PERMISSIONS.TEACHER_VIEW} />,
        children: [
          {
            element: <DashboardLayout />,
            children: [
              {
                path: '/teachers',
                element: <TeachersLayout />,
                children: [
                  { index: true, element: <TeacherDashboardPage /> },
                  { path: 'list', element: <TeacherListPage /> },
                  { path: 'add', element: <AddTeacherPage /> },
                  { path: 'assignments', element: <TeacherAssignmentsPage /> },
                  { path: ':id', element: <TeacherProfilePage /> },
                  { path: ':id/edit', element: <EditTeacherPage /> },
                ]
              },
            ],
          }
        ],
      },
      
      // ── Protected Staff Management Routes ────────────
      {
        element: <ProtectedRoute requiredPermission={PERMISSIONS.STAFF_MANAGE_VIEW} />,
        children: [
          {
            element: <DashboardLayout />,
            children: [
              {
                path: '/staff-management',
                element: <StaffManagementLayout />,
                children: [
                  { index: true, element: <StaffListPage /> },
                  { path: 'departments', element: <DepartmentsPage /> },
                  { path: 'designations', element: <DesignationsPage /> },
                  { path: 'leave-types', element: <LeaveTypesPage /> },
                  { path: 'discipline', element: <StaffDisciplinePage /> },

                  { path: 'staff/add', element: <AddEditStaffPage /> },
                  { path: 'staff/edit/:id', element: <AddEditStaffPage /> },
                  { path: 'staff/profile/:id', element: <StaffProfilePage /> },
                ]
              },
            ],
          }
        ],
      },
      
      // ── Protected Teacher Directory Route ────────────
      {
        element: <ProtectedRoute requiredPermission={PERMISSIONS.TEACHER_DIRECTORY_VIEW} />,
        children: [
          {
            element: <DashboardLayout />,
            children: [
              {
                path: '/teacher-directory',
                element: <TeacherDirectoryPage />,
              }
            ],
          }
        ],
      },

      // ── Protected Teacher Workspace Routes ───────────
      {
        // Require at least one teacher workspace permission to enter this block
        element: <ProtectedRoute requiredPermission={PERMISSIONS.TEACHER_OWN_DASHBOARD} />,
        children: [
          {
            element: <DashboardLayout />,
            children: [
              { path: '/my-classes', element: <MyClassesPage /> },
              { path: '/my-students', element: <MyStudentsPage /> },

              { path: '/homework', element: <HomeworkPage /> },
              { path: '/classwork', element: <ClassworkDiaryPage /> },
              { path: '/my-timetable', element: <MyTimetablePage /> },
              { path: '/teacher-notifications', element: <TeacherNotificationsPage /> },
              { path: '/my-profile', element: <MyProfilePage /> },
            ],
          }
        ],
      },

      // ── Protected Leaves Routes ──────────────────────
      {
        element: <ProtectedRoute requiredPermission={[PERMISSIONS.LEAVE_VIEW_OWN, PERMISSIONS.LEAVE_MANAGE]} />,
        children: [
          {
            element: <DashboardLayout />,
            children: [
              {
                path: '/leaves',
                element: <LeavesLayout />,
                children: [
                  { index: true, element: <LeaveRequestsPage /> },
                  { path: 'requests', element: <LeaveRequestsPage /> },
                  { path: 'my', element: <MyLeavesPage /> },
                ]
              },
            ],
          }
        ],
      },

      // ── Protected Attendance Routes ──────────────────
      {
        element: <ProtectedRoute requiredPermission={PERMISSIONS.DASHBOARD_VIEW} />, // Assuming basic view permission for now
        children: [
          {
            element: <DashboardLayout />,
            children: [
              {
                path: '/attendance',
                element: <AttendanceLayout />,
                children: [
                  { index: true, element: <DailyAttendancePage /> },
                  { path: 'daily', element: <DailyAttendancePage /> },
                  { path: 'staff/:staffId', element: <StaffAttendanceDetailsPage /> },
                  { path: 'my-history', element: <MyAttendanceHistoryPage /> },
                  { path: 'my_history', element: <MyAttendanceHistoryPage /> },
                  { path: 'my_attendance_history', element: <MyAttendanceHistoryPage /> },
                  { path: 'history', element: <MyAttendanceHistoryPage /> },
                  { path: 'my-attendance', element: <MyAttendancePage /> },
                  { path: 'my_attendance', element: <MyAttendancePage /> },
                  { path: 'student-attendance', element: <StudentAttendanceDailyPage /> },
                  { path: 'student_attendance', element: <StudentAttendanceDailyPage /> },
                ]
              },
            ],
          }
        ],
      },

      // ── Staff Attendance Settings (Principal & Org Admin only) ────
      {
        element: <ProtectedRoute requiredPermission={PERMISSIONS.STAFF_ATTENDANCE_SETTINGS} />,
        children: [
          {
            element: <DashboardLayout />,
            children: [
              {
                path: '/attendance/settings',
                element: <AttendanceSettingsPage />,
              },
              {
                path: '/settings/staff-attendance',
                element: <AttendanceSettingsPage />,
              },
            ],
          }
        ],
      },

      // ── Student Attendance Settings (Principal & Org Admin only) ─
      {
        element: <ProtectedRoute requiredPermission={PERMISSIONS.STUDENT_ATTENDANCE_SETTINGS} />,
        children: [
          {
            element: <DashboardLayout />,
            children: [
              {
                path: '/student-attendance-settings',
                element: <StudentAttendanceSettingsPage />,
              },
              {
                path: '/settings/student-attendance',
                element: <StudentAttendanceSettingsPage />,
              },
            ],
          }
        ],
      },

      // ── Settings Hub Route (Principal & Org Admin / Settings Manage) ──
      {
        element: <ProtectedRoute requiredPermission={PERMISSIONS.SETTINGS_MANAGE} />,
        children: [
          {
            element: <DashboardLayout />,
            children: [
              {
                path: '/settings',
                element: <SettingsPage />,
              },
            ],
          }
        ],
      },

      // ── Protected Expense Management Routes ──────────
      {
        element: <ProtectedRoute requiredPermission={PERMISSIONS.DASHBOARD_VIEW} />,
        children: [
          {
            element: <DashboardLayout />,
            children: [
              {
                path: '/expenses/dashboard',
                element: <ExpenseDashboardPage />,
              },
              {
                path: '/expenses',
                element: <ExpenseListPage />,
              },
              {
                path: '/expenses/categories',
                element: <ExpenseCategoryPage />,
              },
            ],
          }
        ],
      },

      // ── Protected Income Management Routes ───────────
      {
        element: <ProtectedRoute requiredPermission={PERMISSIONS.DASHBOARD_VIEW} />,
        children: [
          {
            element: <DashboardLayout />,
            children: [
              {
                path: '/income/dashboard',
                element: <IncomeDashboardPage />,
              },
              {
                path: '/income',
                element: <IncomeListPage />,
              },
              {
                path: '/income/categories',
                element: <IncomeCategoryPage />,
              },
            ],
          }
        ],
      },

      // ── Error Routes ──────────────────────────────
      {
        element: <BlankLayout />,
        children: [
          {
            path: '/unauthorized',
            element: <UnauthorizedPage />,
          },
          {
            path: '*',
            element: <NotFoundPage />,
          },
        ],
      },
    ],
  },
]);

export default router;
